import { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import { ParenthoodVM } from 'shell/view-models/parenthood';
import { transformToChoiceDate } from 'shell/components/shared/choice-date/functions';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, SubjectsList, SubjectData } from 'shell/models/person';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import { CitizenshipVM } from 'shell/view-models/citizenship';
import { BirthdayDocument as BirthdayDocumentVM } from 'shell/view-models/birthday-document';
import {
  AccordionSubjectAttributesVM,
  BodyTablePersonsVM,
  IdentificationDocument,
  PersonalDataVM,
  ResidenceOrigin,
} from 'shell/view-models/personal-data';
import { RegisterOfficeVM } from 'shell/view-models/register-office-practice-vm';

export const idPaternity = 1;

export const idMaternity = 2;

const transformParenthood = (person: SubjectData): Array<ParenthoodVM> | undefined => {
  const birthChoiceDateFather = transformToChoiceDate(
    person.paternita?.generalita?.senzaGiorno,
    person.paternita?.generalita?.senzaGiornoMese
  );

  const paternity: ParenthoodVM | undefined = person.paternita
    ? {
        id: idPaternity,
        surname: person.paternita?.generalita?.cognome || '',
        name: person.paternita?.generalita?.nome || '',
        fiscalCode: person.paternita?.generalita?.codiceFiscale?.codFiscale || '',
        dateBirth: person.paternita?.generalita?.dataNascita
          ? DateTime.fromISO(person.paternita?.generalita?.dataNascita).toFormat('dd/MM/yyyy')
          : '',
        generality: {
          aire: person.paternita?.generalita?.soggettoAIRE,
          validityFiscalCode: person.paternita?.generalita?.codiceFiscale?.validitaCF,
          validityDateFiscalCode: person.paternita?.generalita?.codiceFiscale
            ?.dataAttribuzioneValidita
            ? DateTime.fromISO(
                person.paternita?.generalita?.codiceFiscale?.dataAttribuzioneValidita
              )
            : null,
          gender: person.paternita?.generalita?.sesso || '',
          expatriationYear: person.paternita?.generalita?.annoEspatrio || '',
          birthDate: person.paternita?.generalita?.dataNascita
            ? DateTime.fromISO(person.paternita?.generalita?.dataNascita)
            : null,
          // senza giorno mese, etc
          birthChoiceDate: birthChoiceDateFather,
          // generality italian form
          italian: {
            location:
              person.paternita?.generalita?.luogoNascita?.localita?.descrizioneLocalita || '',
            province: person.paternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
            // municipality: person.paternita?.generalita?.luogoNascita?.comune?.nomeComune || '',
            municipality: {
              description: person.paternita?.generalita?.luogoNascita?.comune?.nomeComune || '',
              province:
                person.paternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
            },
          },
          // generality foreign form
          foreign: {
            state: {
              id: person.paternita?.generalita?.luogoNascita?.localita?.codiceStato || '',
              value: person.paternita?.generalita?.luogoNascita?.localita?.descrizioneStato || '',
            },
            province: person.paternita?.generalita?.luogoNascita?.localita?.provinciaContea || '',
            municipality: {
              description:
                person.paternita?.generalita?.luogoNascita?.localita?.descrizioneLocalita || '',
              province: person.paternita?.generalita?.luogoNascita?.localita?.provinciaContea || '',
            },
          },
          // Other fields generality
          exceptionalBirthPlace: person.paternita?.generalita?.luogoNascita?.luogoEccezionale || '',
          municipalityIdentifier:
            person.paternita?.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
          istatCode:
            person.paternita?.generalita?.idSchedaSoggettoComune?.idSchedaSoggettoComuneIstat || '',
          anprIdentifier: person.paternita?.generalita?.idSchedaSoggettoANPR || '',
          note: person.paternita?.generalita?.note || '',
          fiscalCode: person.paternita?.generalita?.codiceFiscale?.codFiscale || '',
          name: person.paternita?.generalita?.nome || '',
          surname: person.paternita?.generalita?.cognome || '',
          withoutName: person.paternita?.generalita?.senzaNome === '1' ? true : false,
          withoutSurname: person.paternita?.generalita?.senzaCognome === '1' ? true : false,
        },
        person: {
          fiscalCode: person.paternita?.generalita?.codiceFiscale?.codFiscale || '',
          name: person.paternita?.generalita?.nome || '',
          surname: person.paternita?.generalita?.cognome || '',
          withoutName: person.paternita?.generalita?.senzaNome === '1' ? true : false,
          withoutSurname: person.paternita?.generalita?.senzaCognome === '1' ? true : false,
        },
        personInformation: {
          maritalStatus: person.paternita?.statoCivile?.statoCivile || '',
          withoutMaritalStatus: person.paternita?.statoCivile?.statoCivileND === '1' ? true : false,
          maritalStatusNote: person.paternita?.statoCivile?.noteStatoCivile || '',
          code: person.paternita?.cittadinanza?.codiceStato || '',
          citizenship: person.paternita?.cittadinanza?.descrizioneStato || '',
          validityFrom: person.paternita?.cittadinanza?.dataValidita
            ? DateTime.fromISO(person.paternita?.cittadinanza?.dataValidita)
            : null,
          citizenshipNote: person.paternita?.cittadinanza?.noteStato || '',
          qualification: person.paternita?.altriDati?.titoloStudio || '',
          jobPosition: person.paternita?.altriDati?.posizioneProfessionale || '',
          conditionNotProfessional: person.paternita?.altriDati?.condizioneNonProfessionale || '',
        },
      }
    : undefined;

  const birthChoiceDateMother = transformToChoiceDate(
    person.maternita?.generalita?.senzaGiorno,
    person.maternita?.generalita?.senzaGiornoMese
  );

  const maternity: ParenthoodVM | undefined = person.maternita
    ? {
        id: idMaternity,
        surname: person.maternita?.generalita?.cognome || '',
        name: person.maternita?.generalita?.nome || '',
        fiscalCode: person.maternita?.generalita?.codiceFiscale?.codFiscale || '',
        dateBirth: person.maternita?.generalita?.dataNascita
          ? DateTime.fromISO(person.maternita?.generalita?.dataNascita).toFormat('dd/MM/yyyy')
          : '',
        generality: {
          aire: person.maternita?.generalita?.soggettoAIRE,
          validityFiscalCode: person.maternita?.generalita?.codiceFiscale?.validitaCF,
          validityDateFiscalCode: person.maternita?.generalita?.codiceFiscale
            ?.dataAttribuzioneValidita
            ? DateTime.fromISO(
                person.maternita?.generalita?.codiceFiscale?.dataAttribuzioneValidita
              )
            : null,
          gender: person.maternita?.generalita?.sesso || '',
          expatriationYear: person.maternita?.generalita?.annoEspatrio || '',
          birthDate: person.maternita?.generalita?.dataNascita
            ? DateTime.fromISO(person.maternita?.generalita?.dataNascita)
            : null,
          // senza giorno mese, etc
          birthChoiceDate: birthChoiceDateMother,
          // generality italian form
          italian: {
            location:
              person.maternita?.generalita?.luogoNascita?.localita?.descrizioneLocalita || '',
            province: person.maternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
            // municipality: person.maternita?.generalita?.luogoNascita?.comune?.nomeComune || '',
            municipality: {
              description: person.maternita?.generalita?.luogoNascita?.comune?.nomeComune || '',
              province:
                person.maternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
            },
          },
          // generality foreign form
          foreign: {
            state: {
              id: person.maternita?.generalita?.luogoNascita?.localita?.codiceStato || '',
              value: person.maternita?.generalita?.luogoNascita?.localita?.descrizioneStato || '',
            },
            province: person.maternita?.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
            municipality: {
              description:
                person.maternita?.generalita?.luogoNascita?.localita?.descrizioneLocalita || '',
              province: person.maternita?.generalita?.luogoNascita?.localita?.provinciaContea || '',
            },
          },
          // Other fields generality
          exceptionalBirthPlace: person.maternita?.generalita?.luogoNascita?.luogoEccezionale || '',
          municipalityIdentifier:
            person.maternita?.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
          istatCode:
            person.maternita?.generalita?.idSchedaSoggettoComune?.idSchedaSoggettoComuneIstat || '',
          anprIdentifier: person.maternita?.generalita?.idSchedaSoggettoANPR || '',
          note: person.maternita?.generalita?.note || '',
          fiscalCode: person.maternita?.generalita?.codiceFiscale?.codFiscale || '',
          name: person.maternita?.generalita?.nome || '',
          surname: person.maternita?.generalita?.cognome || '',
          withoutName: person.maternita?.generalita?.senzaNome === '1' ? true : false,
          withoutSurname: person.maternita?.generalita?.senzaCognome === '1' ? true : false,
        },
        person: {
          fiscalCode: person.maternita?.generalita?.codiceFiscale?.codFiscale || '',
          name: person.maternita?.generalita?.nome || '',
          surname: person.maternita?.generalita?.cognome || '',
          withoutName: person.maternita?.generalita?.senzaNome === '1' ? true : false,
          withoutSurname: person.maternita?.generalita?.senzaCognome === '1' ? true : false,
        },
        personInformation: {
          maritalStatus: person.maternita?.statoCivile?.statoCivile || '',
          withoutMaritalStatus: person.maternita?.statoCivile?.statoCivileND === '1' ? true : false,
          maritalStatusNote: person.maternita?.statoCivile?.noteStatoCivile || '',
          code: person.maternita?.cittadinanza?.codiceStato || '',
          citizenship: person.maternita?.cittadinanza?.descrizioneStato || '',
          validityFrom: person.maternita?.cittadinanza?.dataValidita
            ? DateTime.fromISO(person.maternita?.cittadinanza?.dataValidita)
            : null,
          citizenshipNote: person.maternita?.cittadinanza?.noteStato || '',
          qualification: person.maternita?.altriDati?.titoloStudio || '',
          jobPosition: person.maternita?.altriDati?.posizioneProfessionale || '',
          conditionNotProfessional: person.maternita?.altriDati?.condizioneNonProfessionale || '',
        },
      }
    : undefined;

  const result = [];

  if (paternity) {
    result.push(paternity);
  }

  if (maternity) {
    result.push(maternity);
  }

  return result;
};

const transformerPersonGenerality = (person: SubjectData): AccordionGeneralityForm => {
  const birthChoiceDate = transformToChoiceDate(
    person.generalita?.senzaGiorno,
    person.generalita?.senzaGiornoMese
  );

  return {
    name: person.generalita?.nome || undefined,
    surname: person.generalita?.cognome || undefined,
    withoutName: person.generalita?.senzaNome === '1' ? true : false,
    withoutSurname: person.generalita?.senzaCognome === '1' ? true : false,
    aire: person.generalita?.soggettoAIRE || '',
    fiscalCode: person.generalita?.codiceFiscale?.codFiscale || undefined,
    validityFiscalCode: person.generalita?.codiceFiscale?.descrizioneValiditaCF || '',
    validityFiscalCodeId: person.generalita?.codiceFiscale?.validitaCF || '',
    validityDateFiscalCode: person.generalita?.codiceFiscale?.dataAttribuzioneValidita
      ? DateTime.fromISO(person.generalita?.codiceFiscale?.dataAttribuzioneValidita)
      : null,
    gender: person.generalita?.sesso || '',
    expatriationYear: person.generalita?.annoEspatrio || '',
    birthDate: person.generalita?.dataNascita
      ? DateTime.fromISO(person.generalita?.dataNascita)
      : null,
    // senza giorno mese, etc
    birthChoiceDate,
    // generality italian form
    italian: {
      location: person.generalita?.luogoNascita?.comune?.descrizioneLocalita || '',
      province: person.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
      municipality: {
        id: person.generalita?.luogoNascita?.comune?.codiceIstat || '',
        description: person.generalita?.luogoNascita?.comune?.nomeComune || '',
        province: person.generalita?.luogoNascita?.comune?.siglaProvinciaIstat || '',
      },
    },
    // generality foreign form
    foreign: {
      state: {
        id: person.generalita?.luogoNascita?.localita?.codiceStato || '',
        value: person.generalita?.luogoNascita?.localita?.descrizioneStato || '',
      },
      province: person.generalita?.luogoNascita?.localita?.provinciaContea || '',
      municipality: {
        id: person.generalita?.luogoNascita?.localita?.codiceStato || '',
        description: person.generalita?.luogoNascita?.localita?.descrizioneLocalita || '',
        province: person.generalita?.luogoNascita?.localita?.provinciaContea || '',
      },
    },
    isForeign: person.generalita?.luogoNascita?.localita ? true : false,
    // Other fields generality
    exceptionalBirthPlace: person.generalita?.luogoNascita?.luogoEccezionale || '',
    municipalityIdentifier: person.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
    istatCode: person.generalita?.idSchedaSoggettoComune?.idSchedaSoggettoComuneIstat || '',
    anprIdentifier: person.generalita?.idSchedaSoggettoANPR || '',
    note: person.generalita?.note || '',
  };
};

const transformerPersonSubjectAttributes = (person: SubjectData): AccordionSubjectAttributesVM => {
  return {
    homeless: person.attributiSoggetto?.senzaFissaDimora === 'S' ? true : false,
    subject: person.attributiSoggetto?.soggettoCertificabile || undefined,
  };
};

const transformerPersonCitizenship = (person: SubjectData): Array<CitizenshipVM> => {
  return (person.cittadinanza || []).map((citizen) => ({
    code: citizen.codiceStato || '',
    citizenship: citizen.descrizioneStato || '',
    note: citizen.noteStato || '',
    validityDate: citizen.dataValidita
      ? DateTime.fromISO(citizen.dataValidita).toFormat('dd/MM/yyyy')
      : '',
  }));
};

const transformBirthdayDocument = (person: SubjectData): BirthdayDocumentVM => {
  return {
    municipality: {
      id: person.attoNascita?.comuneRegistrazione?.codiceIstat || '',
      description: person.attoNascita?.comuneRegistrazione?.nomeComune || '',
      province: person.attoNascita?.comuneRegistrazione?.siglaProvinciaIstat || '',
    },
    istatCode: person.attoNascita?.comuneRegistrazione?.codiceIstat || '',
    province: person.attoNascita?.comuneRegistrazione?.siglaProvinciaIstat || '',
    location: person.attoNascita?.comuneRegistrazione?.descrizioneLocalita || '',
    townHallOffice: person.attoNascita?.ufficioMunicipio || '',
    year: person.attoNascita?.anno || '',
    portion: person.attoNascita?.parte || '',
    serie: person.attoNascita?.serie || '',
    documentNumber: person.attoNascita?.numeroAtto || '',
    volume: person.attoNascita?.volume || '',
    documentDate: person.attoNascita?.dataFormazioneAtto
      ? DateTime.fromISO(person.attoNascita?.dataFormazioneAtto)
      : null,
    transcript: person.attoNascita?.trascritto === '1' ? true : false,
  };
};

//tranformer per documento di riconoscimento del richiedente
const IdentificatioDocumentTransformer = (
  identificationDocument: SubjectData
): IdentificationDocument => {
  return {
    isForeign: Boolean(identificationDocument?.cartaIdentita?.consolatoRilascio?.codiceConsolato),
    documentNumber: identificationDocument?.cartaIdentita?.numero,
    issuedDate: identificationDocument?.cartaIdentita?.dataRilascio
      ? DateTime.fromISO(identificationDocument?.cartaIdentita?.dataRilascio)
      : null,
    municipality: identificationDocument?.cartaIdentita?.comuneRilascio?.nomeComune
      ? {
          id: identificationDocument?.cartaIdentita?.comuneRilascio?.codiceIstat || '',
          description: identificationDocument?.cartaIdentita?.comuneRilascio?.nomeComune || '',
          province:
            identificationDocument?.cartaIdentita?.comuneRilascio?.siglaProvinciaIstat || '',
        }
      : null,
    placeForeign: identificationDocument?.cartaIdentita?.consolatoRilascio?.codiceConsolato || '',
  };
};

//transformer per residenza/provenienza
export const ResidenceOriginTransformer = (value: SubjectData): ResidenceOrigin => {
  const hasEraseData = !!value?.datiCancellazione;

  const irreperibile = value?.datiCancellazione?.motivoCancellazione === '6';

  const hasToFillOrigin = hasEraseData ? irreperibile : true;

  const residence = value?.residenza || [];

  const [provenienza] = residence;

  return {
    irreperibile,
    // neverRegistered: value?.minorePreadozione, TODO
    origin:
      provenienza?.indirizzo?.comune && hasToFillOrigin
        ? {
            id: provenienza?.indirizzo?.comune?.codiceIstat || '',
            description: provenienza?.indirizzo?.comune?.nomeComune || '',
            province: provenienza?.indirizzo?.comune?.siglaProvinciaIstat || '',
          }
        : undefined,
    isForeign: !!provenienza?.localitaEstera?.indirizzoEstero?.localita,
    originState:
      provenienza?.localitaEstera?.indirizzoEstero?.localita && hasToFillOrigin
        ? {
            id: provenienza?.localitaEstera?.indirizzoEstero?.localita?.codiceStato || '',
            province: provenienza?.localitaEstera?.indirizzoEstero?.localita?.provinciaContea || '',
            value: provenienza?.localitaEstera?.indirizzoEstero?.localita?.descrizioneStato || '',
          }
        : undefined,
    originMunicipalityForeign:
      provenienza?.localitaEstera?.indirizzoEstero?.localita && hasToFillOrigin
        ? {
            id: provenienza?.localitaEstera?.indirizzoEstero?.localita?.codiceStato || '',
            description:
              provenienza?.localitaEstera?.indirizzoEstero?.localita?.descrizioneLocalita || '',
            province: provenienza?.localitaEstera?.indirizzoEstero?.localita?.provinciaContea || '',
          }
        : undefined,
    previousOrigin: value?.datiIscrizione?.datiIscrizioneComune?.comune?.nomeComune,
    previousOriginProvince:
      value?.datiIscrizione?.datiIscrizioneComune?.comune?.siglaProvinciaIstat,
    previousOriginCode: value?.datiIscrizione?.datiIscrizioneComune?.comune?.codiceIstat,
  };
};

const transformerPerson = (
  response:
    | Partial<AxiosResponse<HttpResponse<ResponseAnprData>>>
    | Partial<AxiosResponse<HttpResponse<SubjectsList>>> // secondo tipo per doppia ricerca ANPR e DB
): PersonalDataVM | undefined => {
  const datiSoggetto =
    (response?.data?.payload as ResponseAnprData).risposta3002OK?.listaSoggetti?.datiSoggetto ||
    (response?.data?.payload as SubjectsList).datiSoggetto ||
    [];

  if (!datiSoggetto || datiSoggetto.length === 0) {
    // persona non trovata
    return {
      withSearch: false, // la persona non è il risultato della ricerca
      openModalNewPerson: true,
    };
  }

  if (datiSoggetto.length === 1) {
    // una sola persona

    return {
      name: undefined,
      surname: undefined,
      fiscalCode: undefined,
      gender: undefined,
      withoutName: undefined,
      withoutSurname: undefined,
      withSearch: true, // la persona è il risultato della ricerca
      generality: transformerPersonGenerality(datiSoggetto[0]),
      accordionSubjectAttributes: transformerPersonSubjectAttributes(datiSoggetto[0]),
      citizenship: transformerPersonCitizenship(datiSoggetto[0]),
      parenthood: transformParenthood(datiSoggetto[0]),
      birthdayDocument: transformBirthdayDocument(datiSoggetto[0]),
      identificationDocument: IdentificatioDocumentTransformer(datiSoggetto[0]),
      residenceOrigin: {
        ...(ResidenceOriginTransformer(datiSoggetto[0]) || {}),
        irreperibile:
          ResidenceOriginTransformer(datiSoggetto[0]).irreperibile ||
          datiSoggetto[0].datiCancellazione?.motivoCancellazione === '6',
      },
      openModalNewPerson: false,
      subjectData: datiSoggetto[0],
      // true il soggetto proviene da SIPO (DB locale)
      isSipo:
        Boolean(
          datiSoggetto[0]?.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto &&
            (!datiSoggetto[0]?.generalita?.idSchedaSoggettoANPR ||
              datiSoggetto[0]?.generalita?.idSchedaSoggettoANPR === '0')
        ) ||
        (datiSoggetto[0]?.datiCancellazione &&
          datiSoggetto[0]?.datiCancellazione.motivoCancellazione?.toString() === '6'),
      idSchedaSoggetto: datiSoggetto[0]?.generalita?.idSchedaSoggettoComune?.idSchedaSoggetto,
      datiCancellazione: datiSoggetto[0]?.datiCancellazione,
    } as PersonalDataVM;
  }

  // più persone
  return {
    openModalNewPerson: false,
    table: {
      body: datiSoggetto
        .map((person, index) => {
          const residenceArray = person.residenza || [];

          let birthplace = '';
          // birthplace check for object
          if (person.generalita?.luogoNascita?.localita) {
            birthplace = `${person.generalita?.luogoNascita?.localita.descrizioneLocalita} (${person.generalita?.luogoNascita?.localita.descrizioneStato})`;
          }

          if (person.generalita?.luogoNascita?.comune) {
            birthplace = `${person.generalita?.luogoNascita?.comune.nomeComune} (${person.generalita?.luogoNascita?.comune.siglaProvinciaIstat})`;
          }
          // Residence check for object
          let residence = '';
          // scelgo il primo o è un array?
          if (residenceArray[0]?.indirizzo) {
            residence = `${residenceArray[0]?.indirizzo.comune?.nomeComune || ''} (${
              residenceArray[0]?.indirizzo.comune?.siglaProvinciaIstat || ''
            })`;
          }
          // scelgo il primo o è un array?
          if (residenceArray[0]?.localitaEstera) {
            residence = `${residenceArray[0]?.localitaEstera?.indirizzoEstero?.localita?.descrizioneLocalita} (${residenceArray[0]?.localitaEstera?.indirizzoEstero?.localita?.descrizioneStato})`;
          }

          let state = '';
          // state check for object
          if (residenceArray[0]?.indirizzo?.toponimo) {
            const toponym = residenceArray[0]?.indirizzo?.toponimo;

            const civic = residenceArray[0]?.indirizzo?.numeroCivico;

            state = `${toponym.specie} ${toponym.denominazioneToponimo},${civic?.numero}${
              civic?.esponente1 ? `/${civic?.esponente1}${civic?.lettera}` : ''
            }`;
          }

          if (person.generalita?.soggettoAIRE && person.generalita?.soggettoAIRE === 'S') {
            state = 'Iscritto AIRE';
          }

          if (
            person.datiCancellazione &&
            person.datiCancellazione?.descrizioneMotivoCancellazione
          ) {
            state = `${person.datiCancellazione.descrizioneMotivoCancellazione}`;
          }

          if (person.descrizioneStatusPersonaSipo) {
            state = `${person.descrizioneStatusPersonaSipo}`;
          }

          return {
            // true il soggetto proviene da SIPO (DB locale)
            isSipo:
              Boolean(
                person?.generalita?.idSchedaSoggettoComune &&
                  (!person?.generalita?.idSchedaSoggettoANPR ||
                    person?.generalita?.idSchedaSoggettoANPR === '0')
              ) ||
              (person?.datiCancellazione &&
                person?.datiCancellazione.motivoCancellazione?.toString() === '6'),
            generality: transformerPersonGenerality(person),
            accordionSubjectAttributes: transformerPersonSubjectAttributes(person),
            citizenship: transformerPersonCitizenship(person),
            parenthood: transformParenthood(person),
            birthdayDocument: transformBirthdayDocument(person),
            identificationDocument: IdentificatioDocumentTransformer(datiSoggetto[0]),
            residenceOrigin: ResidenceOriginTransformer(datiSoggetto[0]),
            withSearch: true, // la persona è il risultato della ricerca
            residenceAll: person.residenza || undefined,
            residence,
            code: person.generalita?.codiceFiscale?.codFiscale || (index + 1).toString(),
            fiscalCode: person.generalita?.codiceFiscale?.codFiscale || '',
            surname: person.generalita?.cognome || '',
            name: person.generalita?.nome || '',
            gender: person.generalita?.sesso || '',
            birthday: person.generalita?.dataNascita
              ? DateTime.fromISO(person.generalita.dataNascita).toFormat('dd/MM/yyyy')
              : '',
            birthplace,
            state,
            anpr: person.generalita?.idSchedaSoggettoANPR || '',
            hidden: {
              delete: true,
              edit: true,
            },
          } as BodyTablePersonsVM;
        })
        .map((el) => ({ ...el, item: el })),
    },
  } as PersonalDataVM;
};

export const transformerPersonToRegisterOffice = (
  response: Partial<AxiosResponse<HttpResponse<ResponseAnprData>>>,
  isApplicant?: boolean
): RegisterOfficeVM => {
  const result: PersonalDataVM | undefined = transformerPerson(response);

  if (isApplicant) {
    // richiedente
    return {
      applicantData: result,
    };
  }

  // intestatario
  return {
    accountholderData: result,
    isAire: result?.generality?.aire
      ? result?.generality?.aire === '1' || result?.generality?.aire === 'S'
      : false,
  };
};
